import { styled } from 'styled-system/jsx'

const Card = styled('section', {
  base: {
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#EAE4F8',
    borderRadius: 'lg',
    padding: '20px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    boxShadow: '0 18px 48px -28px rgba(101, 56, 224, 0.22)',
  },
})

const HeaderRow = styled('header', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
})

const HeaderText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
})

const Eyebrow = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: 'quickpass.primary',
  },
})

const Title = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.3px',
  },
})

const Subtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.55',
    maxWidth: '500px',
  },
})

const HeaderActions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
})

const PrimaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '16px',
    paddingY: '9px',
    cursor: 'pointer',
    transition:
      'transform {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _hover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 28px -10px rgba(128, 84, 255, 0.5)',
    },
    _disabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
      _hover: { transform: 'none', boxShadow: 'none' },
    },
  },
})

const SecondaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '8px',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: { borderColor: 'quickpass.primary', color: 'quickpass.primary' },
    _disabled: { opacity: '0.5', cursor: 'not-allowed' },
  },
})

const ParagraphList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const ParagraphBlock = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const ParagraphHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
})

const ParagraphLabel = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: 'quickpass.ink',
  },
})

const ParagraphDescription = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
  },
})

const ParagraphCopyButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: 'quickpass.primary',
    backgroundColor: 'transparent',
    border: '1px solid',
    borderColor: 'quickpass.primary',
    borderRadius: 'pill',
    paddingX: '10px',
    paddingY: '4px',
    cursor: 'pointer',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      backgroundColor: 'quickpass.primary',
      color: '#FFFFFF',
    },
  },
})

const ParagraphTextarea = styled('textarea', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    backgroundColor: 'quickpass.surfaceCool',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'md',
    paddingX: '14px',
    paddingY: '12px',
    minHeight: '110px',
    lineHeight: '1.55',
    resize: 'vertical',
    transition: 'border-color {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      backgroundColor: '#FFFFFF',
    },
  },
})

const Footer = styled('footer', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
    paddingTop: '8px',
    borderTop: '1px dashed',
    borderColor: '#EAE4F8',
  },
})

const StampText = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
  },
})

const CopyAllFlash = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    color: 'quickpass.success',
  },
})

const StatusText = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
  },
})

const ErrorText = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.warmth',
    fontWeight: '600',
  },
})

const SkeletonLine = styled('div', {
  base: {
    backgroundColor: 'rgba(128, 84, 255, 0.12)',
    borderRadius: 'sm',
    height: '12px',
  },
})

const PremiumBadge = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: '#7B47E0',
    background:
      'linear-gradient(135deg, rgba(128, 84, 255, 0.18) 0%, rgba(193, 109, 255, 0.18) 100%)',
    border: '1px solid rgba(128, 84, 255, 0.3)',
    borderRadius: 'pill',
    paddingX: '8px',
    paddingY: '3px',
  },
})

export {
  Card,
  HeaderRow,
  HeaderText,
  Eyebrow,
  Title,
  Subtitle,
  HeaderActions,
  PrimaryButton,
  SecondaryButton,
  ParagraphList,
  ParagraphBlock,
  ParagraphHeader,
  ParagraphLabel,
  ParagraphDescription,
  ParagraphCopyButton,
  ParagraphTextarea,
  Footer,
  StampText,
  CopyAllFlash,
  StatusText,
  ErrorText,
  SkeletonLine,
  PremiumBadge,
}
